/**
 * Bundle builder - on-page behaviour (Carbonize, 2026-09-10)
 * ==========================================================
 * Draws the pickers with the theme's OWN variant-picker markup (.color-swatch / .block-swatch), keeps the
 * price summary up to date, drives the shell product's gallery, and adds every slot to the cart in one request.
 * It contains no rules about which combinations exist - assets/bundle-resolver.js owns that.
 */
(function () {
  'use strict';

  function money(cents, format) {
    var value = (cents / 100).toFixed(2);
    if (!format) return '$' + value;
    return format
      .replace(/<[^>]+>/g, '')
      .replace(/\{\{\s*amount\s*\}\}/g, value)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(cents / 100))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, value.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, Math.round(cents / 100));
  }

  function el(tag, className, attrs) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    Object.keys(attrs || {}).forEach(function (k) {
      if (attrs[k] === true) node.setAttribute(k, '');
      else if (attrs[k] !== false && attrs[k] != null) node.setAttribute(k, attrs[k]);
    });
    return node;
  }

  var uid = 0;

  function BundleBuilderUI(root) {
    this.root = root;
    this.section = root.closest('.shopify-section') || document;
    this.moneyFormat = root.getAttribute('data-money-format') || '';
    this.buttonText = root.getAttribute('data-button-text') || 'Add bundle to cart';
    this.incompleteMessage = root.getAttribute('data-incomplete-message') || 'Choose your items to continue';
    this.galleryFollow = root.getAttribute('data-gallery-follow') || 'last';
    this.roundSwatches = root.getAttribute('data-swatch-style') === 'round';
    this.cartType = root.getAttribute('data-cart-type') || 'drawer';
    this.discountCode = (root.getAttribute('data-discount-code') || '').trim();

    var dataNode = root.querySelector('[data-bundle-data]');
    if (!dataNode) return;
    var config;
    try { config = JSON.parse(dataNode.textContent); }
    catch (error) { console.error('[bundle-builder] Could not read the bundle data.', error); return; }

    this.bundle = new window.BundleResolver.Bundle(config);
    this.submit = root.querySelector('[data-bundle-submit]');
    this.errorNode = root.querySelector('[data-bundle-error]');
    this.idPrefix = 'bundle-' + (++uid) + '-';
    this.idSeq = 0;

    var preselect = root.getAttribute('data-preselect') === 'true';
    this.bundle.slots.forEach(function (slot) {
      if (preselect || slot.shape === 'single') slot.preselectFirstAvailable();
    });

    this.render();
    this.bindSubmit();
    if (window.Shopify && window.Shopify.designMode) this.lint();
  }

  /* ---------------------------------------------------------------- render */

  BundleBuilderUI.prototype.render = function () {
    var self = this;
    this.bundle.slots.forEach(function (slot) {
      var slotNode = self.root.querySelector('[data-bundle-slot="' + slot.index + '"]');
      if (!slotNode) return;
      self.renderPicker(slotNode, slot, 'material', slotNode.getAttribute('data-style') || 'swatches');
      self.renderPicker(slotNode, slot, 'model', slotNode.getAttribute('data-model-style') || 'stacked');
      self.renderSlotSummary(slotNode, slot);
    });
    this.renderSummary();
  };

  BundleBuilderUI.prototype.renderPicker = function (slotNode, slot, axis, style) {
    var container = slotNode.querySelector('[data-bundle-picker="' + axis + '"]');
    if (!container) return;
    container.innerHTML = '';

    var options = axis === 'material' ? slot.getMaterialOptions() : slot.getModelOptions();
    if (slot.shape === 'single' || options.length === 0 || (axis === 'model' && slot.shape !== 'model_material')) {
      container.hidden = true;
      return;
    }
    container.hidden = false;

    if (axis === 'model' && slot.hideUnavailableModels) {
      // Sold-out models stay visible (struck through); models that do not exist for this material go away.
      options = options.filter(function (o) { return o.exists; });
    }

    if (style === 'dropdown') {
      container.appendChild(this.buildDropdown(slot, axis, options));
      return;
    }

    if (axis === 'material') {
      container.appendChild(this.buildRow(slot, axis, options, style === 'swatches' ? 'swatch' : 'block', 'stacked'));
      return;
    }

    // Models: one row per brand when the slot mixes brands (iPhone / Samsung / Google).
    var brands = [];
    options.forEach(function (o) { if (brands.indexOf(o.brand) === -1) brands.push(o.brand); });
    var self = this;
    brands.forEach(function (brand) {
      var group = el('div', 'bundle-slot__group v-stack gap-1');
      if (brands.length > 1 && brand) {
        var title = el('p', 'text-subdued text-sm bundle-slot__group-title');
        title.textContent = brand;
        group.appendChild(title);
      }
      group.appendChild(self.buildRow(slot, axis, options.filter(function (o) { return o.brand === brand; }), 'block', style));
      container.appendChild(group);
    });
  };

  /**
   * A row of choices using the theme's classes. type = 'swatch' (round picture) or 'block' (text pill).
   * layout = 'stacked' (wraps) or 'scroll' (single scrolling row, like the theme's un-stacked picker).
   */
  BundleBuilderUI.prototype.buildRow = function (slot, axis, options, type, layout) {
    var self = this;
    var row = el('div', 'variant-picker__option-values ' + (layout === 'scroll' ? 'scroll-area bleed sm:unbleed' : 'wrap') + ' gap-2', { role: 'group' });
    var name = this.idPrefix + slot.index + '-' + axis;

    options.forEach(function (option) {
      // Unique per row AND per brand group (models are split into one row per brand, sharing the radio name).
      var id = name + '-' + (++self.idSeq);
      var classes = type === 'swatch'
        ? 'color-swatch' + (self.roundSwatches ? ' rounded-full' : '')
        : 'block-swatch';
      if (option.disabled) classes += ' is-disabled';
      if (option.selected) classes += ' is-selected';

      var label;
      if (option.disabled) {
        // No input: nothing to click. Theme styling greys + strikes it through.
        label = el('span', classes, { 'aria-disabled': 'true', title: option.label + (option.exists ? ' - sold out' : ' - not available') });
      } else {
        var input = el('input', 'sr-only', { type: 'radio', name: name, id: id, value: option.value });
        if (option.selected) input.checked = true;
        input.addEventListener('change', function () { self.choose(slot, axis, option.value); });
        row.appendChild(input);
        label = el('label', classes, { for: id });
      }

      if (type === 'swatch') {
        if (option.image) label.style.setProperty('--swatch-background', 'url(' + option.image + ')');
        label.setAttribute('data-tooltip', option.label);
        var sr = el('span', 'sr-only'); sr.textContent = option.label; label.appendChild(sr);
      } else {
        var text = el('span'); text.textContent = option.label; label.appendChild(text);
      }
      row.appendChild(label);
    });

    if (layout === 'scroll') {
      var wrap = el('div', 'scroll-area bleed sm:unbleed');
      wrap.appendChild(row);
      return wrap;
    }
    return row;
  };

  BundleBuilderUI.prototype.buildDropdown = function (slot, axis, options) {
    var select = el('select', 'bundle-select', { 'aria-label': slot.label });
    var placeholder = el('option', null, { value: '' });
    placeholder.textContent = axis === 'model' ? 'Choose your model' : 'Choose';
    select.appendChild(placeholder);
    var self = this, lastBrand = null, target = select;
    options.forEach(function (option) {
      if (axis === 'model' && option.brand && option.brand !== lastBrand) {
        target = el('optgroup', null, { label: option.brand });
        select.appendChild(target);
        lastBrand = option.brand;
      }
      var opt = el('option', null, { value: option.value, disabled: option.disabled });
      opt.textContent = option.disabled ? option.label + (option.exists ? ' (sold out)' : ' (not available)') : option.label;
      if (option.selected) opt.selected = true;
      target.appendChild(opt);
    });
    select.addEventListener('change', function () { if (select.value) self.choose(slot, axis, select.value); });
    return select;
  };

  BundleBuilderUI.prototype.renderSlotSummary = function (slotNode, slot) {
    var resolved = slot.resolve();
    var chosen = slotNode.querySelector('[data-bundle-chosen]');
    var thumb = slotNode.querySelector('[data-bundle-thumb]');
    var self = this;

    if (chosen) {
      if (resolved) {
        var bits = [resolved.materialName];
        if (resolved.modelName) bits.push(resolved.modelName);
        chosen.textContent = bits.join(' · ') + ' — ' + money(resolved.price, this.moneyFormat);
      } else {
        chosen.textContent = '';
      }
    }

    if (thumb) {
      if (resolved && resolved.image) {
        thumb.src = resolved.image;
        thumb.alt = resolved.productTitle;
        thumb.hidden = false;
        thumb.style.cursor = 'pointer';
        thumb.onclick = function () { self.showInGallery(slot, resolved); };
      } else {
        thumb.hidden = true;
      }
    }
  };

  BundleBuilderUI.prototype.renderSummary = function () {
    var pricing = this.bundle.pricing();
    var totalRow = this.root.querySelector('[data-bundle-total-row]');
    var totalNode = this.root.querySelector('[data-bundle-full-total]');
    if (totalRow && totalNode) {
      totalRow.hidden = !this.bundle.isComplete();
      totalNode.textContent = money(pricing.fullTotal, this.moneyFormat);
    }
    var priceNode = this.root.querySelector('[data-bundle-price]');
    if (priceNode && pricing.dynamic) {
      // "Second item free" style bundle: the price depends on what was picked.
      priceNode.textContent = this.bundle.isComplete() ? money(pricing.bundlePrice, this.moneyFormat) : '—';
    }
    var savings = this.root.querySelector('[data-bundle-savings]');
    if (savings) {
      savings.hidden = !pricing.showSavings;
      if (pricing.showSavings) savings.textContent = 'You save ' + money(pricing.savings, this.moneyFormat);
    }
    if (this.submit) {
      var complete = this.bundle.isComplete();
      this.submit.disabled = !complete;
      if (complete) {
        this.submit.textContent = this.buttonText;
      } else {
        var waiting = this.bundle.firstUnresolvedSlot();
        this.submit.textContent = waiting && waiting.prompt ? waiting.prompt : this.incompleteMessage;
      }
    }
  };

  BundleBuilderUI.prototype.choose = function (slot, axis, value) {
    slot.select(axis, value);
    this.render();
    var resolved = slot.resolve();
    if (this.galleryFollow === 'last' || (this.galleryFollow === 'slot_1' && slot.index === 1)) {
      // A material pick is enough to show the picture, even before the model is chosen.
      this.showInGallery(slot, resolved || { materialName: axis === 'material' ? value : slot.selection.material, brand: '' });
    }
  };

  /* --------------------------------------------------------------- gallery
   * Finds the shell photo whose alt text names the chosen carbon type, e.g. "Wallet - Forged Red" or
   * "Case - 3K Twill (Samsung)", then asks the theme's own carousel to show it. Preference order:
   * alt mentions the slot (wallet / case / key organizer) and the brand, then slot only, then any match.
   */
  BundleBuilderUI.prototype.showInGallery = function (slot, resolved) {
    if (!resolved || !resolved.materialName) return;
    var material = String(resolved.materialName).toLowerCase();
    var brand = String(resolved.brand || '').toLowerCase();
    var slotWords = (slot.label + ' ' + slot.collectionTitle).toLowerCase().split(/[^a-z]+/).filter(function (w) {
      return w.length > 3 && ['choose', 'your', 'series', 'bundle', 'money', 'clip', 'lite'].indexOf(w) === -1;
    });

    var candidates = this.bundle.shellMedia.filter(function (m) {
      return m.type === 'image' && String(m.alt || '').toLowerCase().indexOf(material) !== -1;
    });
    if (candidates.length === 0) return;

    function score(m) {
      var alt = m.alt.toLowerCase(), s = 0;
      if (slotWords.some(function (w) { return alt.indexOf(w) !== -1; })) s += 2;
      if (brand && alt.indexOf(brand) !== -1) s += 1;
      if (alt.indexOf('bundle') !== -1) s -= 1; // composite "Bundle Deals" shots rank below the item's own photo
      return s;
    }
    candidates.sort(function (a, b) { return score(b) - score(a) || a.position - b.position; });
    this.selectMedia(candidates[0].position);
  };

  BundleBuilderUI.prototype.selectMedia = function (position) {
    var gallery = this.section.querySelector('product-gallery');
    if (!gallery) return;
    gallery.querySelectorAll('media-carousel').forEach(function (carousel) {
      if (typeof carousel.select === 'function') carousel.select(position - 1, { animate: true });
    });
  };

  /* ------------------------------------------------------------------ cart */

  BundleBuilderUI.prototype.bindSubmit = function () {
    if (!this.submit) return;
    var self = this;
    this.submit.addEventListener('click', function () {
      var key = self.bundle.id + '-' + Date.now().toString(36);
      var items = self.bundle.toCartItems(key);
      if (!items) return;

      self.submit.disabled = true;
      self.submit.setAttribute('aria-busy', 'true');
      if (self.errorNode) self.errorNode.hidden = true;

      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      // Same dance as the theme's own product form, so the cart drawer opens and re-renders itself.
      var sections = ['variant-added'];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sections } }));

      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: items, sections: sections.join(','), sections_url: window.location.pathname })
      })
        .then(function (response) {
          return response.json().then(function (body) {
            if (!response.ok) throw new Error(body.description || body.message || 'Could not add to cart.');
            return body;
          });
        })
        .then(function (added) {
          // Page-only offers: apply the bundle's discount code to the cart so the checkout shows it.
          if (!self.discountCode) return added;
          return fetch(root + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ discount: self.discountCode })
          }).then(function () { return added; }, function () { return added; });
        })
        .then(function (added) {
          if (self.cartType === 'page') { window.location.href = root + 'cart'; return; }
          return fetch(root + 'cart.js').then(function (r) { return r.json(); }).then(function (cart) {
            cart.sections = added.sections;
            document.documentElement.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: added.items || [added], cart: cart } }));
            document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart: cart } }));
          });
        })
        .catch(function (error) {
          if (self.errorNode) { self.errorNode.textContent = error.message; self.errorNode.hidden = false; }
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        })
        .then(function () {
          self.submit.removeAttribute('aria-busy');
          self.renderSummary();
        });
    });
  };

  /* ---------------------------------------------------------------- editor */

  BundleBuilderUI.prototype.lint = function () {
    var node = this.root.querySelector('[data-bundle-lint]');
    if (!node) return;
    var problems = [], self = this;
    this.bundle.slots.forEach(function (slot) {
      var name = slot.label || ('Slot ' + slot.index);
      if (slot.products.length === 0) {
        problems.push(name + ': no usable products. Check the collection is published to the Online Store and that any "always use this variant" value matches an option exactly.');
        return;
      }
      var prices = [];
      slot.products.forEach(function (p) { p.variants.forEach(function (v) { prices.push(v.price); }); });
      var min = Math.min.apply(null, prices), max = Math.max.apply(null, prices);
      if (max - min > 1000) {
        problems.push(name + ': products range from ' + money(min, self.moneyFormat) + ' to ' + money(max, self.moneyFormat) + '. With one fixed bundle price the value differs a lot by pick - consider a collection where prices are closer.');
      }
      var withOne = slot.products.filter(function (p) { return p.variants.length === 1; }).length;
      if (withOne > 0 && withOne < slot.products.length) {
        problems.push(name + ': some products have variants and some do not, so the picker shape cannot be worked out reliably.');
      }
    });
    if (this.bundle.shellMedia.filter(function (m) { return m.alt; }).length === 0) {
      problems.push('Gallery follow: none of this product\'s photos has alt text, so the gallery cannot jump to the chosen carbon type. Label photos like "Wallet - Forged Red".');
    }
    node.hidden = problems.length === 0;
    if (problems.length) {
      node.innerHTML = '<strong>Check this bundle</strong><ul style="margin:.5rem 0 0;padding-left:1.1rem">' +
        problems.map(function (p) { var li = document.createElement('li'); li.textContent = p; return li.outerHTML; }).join('') + '</ul>';
    }
  };

  function init(scope) {
    (scope || document).querySelectorAll('[data-bundle-builder]').forEach(function (root) {
      if (root.dataset.bundleReady === 'true' || !window.BundleResolver) return;
      root.dataset.bundleReady = 'true';
      new BundleBuilderUI(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); });
  else init(document);
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
